import { useMemo, useState } from "react";
import {
    DialogClose,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog.tsx";
import { Button } from "@/components/ui/button.tsx";
import { ManagedForm } from "@/components/ui/form.tsx";
import { useForm } from "react-hook-form";
import { Spinner } from "@/components/ui/spinner.tsx";
import type { Permission, Team, TeamMember } from "@/store/teamStore.ts";
import { setMemberPermissions } from "@/api/teamApi.ts";
import { toast } from "sonner";
import { popDialog } from "@/store/dialogStore.tsx";
import { cn } from "@/lib/utils.ts";
import { Network, Server, Users } from "lucide-react";

type EditMemberPermissionsDialogProps = {
    team: Team;
    member: TeamMember;
    onSaved?: () => void;
};

type PermissionGroupsForm = {
    manageTeam: boolean;
    manageNodes: boolean;
    manageServers: boolean;
};

const GROUPS: Array<{
    key: keyof PermissionGroupsForm;
    label: string;
    permissions: Permission[];
}> = [
    {
        key: "manageTeam",
        label: "Manage Team",
        permissions: ["MANAGE_TEAM"],
    },
    {
        key: "manageNodes",
        label: "Manage Nodes",
        permissions: ["ACCESS_NODES", "MANAGE_NODES"],
    },
    {
        key: "manageServers",
        label: "Manage Servers",
        permissions: [
            "ACCESS_SERVERS",
            "MANAGE_SERVERS",
            "ACCESS_PROFILES",
            "MANAGE_PROFILES",
        ],
    },
];

export function EditMemberPermissionsDialog(
    props: EditMemberPermissionsDialogProps,
) {
    const [saving, setSaving] = useState(false);

    const defaults = useMemo<PermissionGroupsForm>(() => {
        return {
            manageTeam: props.member.permissions.includes("MANAGE_TEAM"),
            manageNodes: ["ACCESS_NODES", "MANAGE_NODES"].some((p) =>
                props.member.permissions.includes(p as Permission),
            ),
            manageServers: (
                [
                    "ACCESS_SERVERS",
                    "MANAGE_SERVERS",
                    "ACCESS_PROFILES",
                    "MANAGE_PROFILES",
                ] as Permission[]
            ).some((p) => props.member.permissions.includes(p)),
        };
    }, [props.member.permissions]);

    const form = useForm<PermissionGroupsForm>({ defaultValues: defaults });

    async function onSave(values: PermissionGroupsForm) {
        const selected = new Set<Permission>();
        if (values.manageTeam) selected.add("MANAGE_TEAM");
        if (values.manageNodes) {
            selected.add("ACCESS_NODES");
            selected.add("MANAGE_NODES");
        }
        if (values.manageServers) {
            selected.add("ACCESS_SERVERS");
            selected.add("MANAGE_SERVERS");
            selected.add("ACCESS_PROFILES");
            selected.add("MANAGE_PROFILES");
        }
        selected.add("ACCESS_TEAM");
        setSaving(true);
        try {
            await setMemberPermissions(
                props.team.id,
                props.member.id,
                Array.from(selected),
            );
            toast.success("Permissions updated successfully.");
            props.onSaved?.();
            popDialog();
        } finally {
            setSaving(false);
        }
    }

    return (
        <ManagedForm form={form} onSubmit={onSave}>
            <DialogHeader>
                <DialogTitle>Edit Permissions</DialogTitle>
                <DialogDescription>
                    Update permissions for {props.member.name} (
                    {props.member.email})
                </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 py-2 max-w-[400px] m-auto lg:m-0 lg:max-w-[870px]">
                {GROUPS.map((g) => {
                    const selected = form.watch(g.key);
                    const Icon =
                        g.key === "manageTeam"
                            ? Users
                            : g.key === "manageNodes"
                              ? Network
                              : Server;
                    return (
                        <div
                            key={g.key}
                            role="button"
                            tabIndex={0}
                            aria-pressed={selected}
                            onClick={() =>
                                form.setValue(g.key, !selected, {
                                    shouldDirty: true,
                                })
                            }
                            onKeyDown={(e) => {
                                if (e.key === " " || e.key === "Enter") {
                                    e.preventDefault();
                                    form.setValue(g.key, !selected, {
                                        shouldDirty: true,
                                    });
                                }
                            }}
                            className={cn(
                                "border rounded-md p-4 flex flex-col gap-2 cursor-pointer transition-colors",
                                "hover:bg-secondary/60",
                                selected &&
                                    "ring-2 ring-primary bg-primary/5 border-primary/50",
                            )}>
                            <div className="flex items-center gap-3">
                                <div
                                    className={cn(
                                        "rounded-md p-2",
                                        selected
                                            ? "bg-primary text-primary-foreground"
                                            : "bg-secondary text-secondary-foreground",
                                    )}>
                                    <Icon className="size-5" />
                                </div>
                                <div className="font-medium">{g.label}</div>
                                <div
                                    className={cn(
                                        "ml-auto text-xs",
                                        selected
                                            ? "text-primary"
                                            : "text-muted-foreground",
                                    )}>
                                    {selected ? "Enabled" : "Disabled"}
                                </div>
                            </div>
                            <div className="text-sm text-muted-foreground">
                                {g.key === "manageTeam" &&
                                    "Invite/kick members, rename, and manage team settings."}
                                {g.key === "manageNodes" &&
                                    "Access and manage nodes connected to this team."}
                                {g.key === "manageServers" &&
                                    "Access/manage servers and profiles within the team."}
                            </div>
                        </div>
                    );
                })}
            </div>
            <DialogFooter>
                <DialogClose asChild>
                    <Button disabled={saving} variant="tertiary">
                        Cancel
                    </Button>
                </DialogClose>
                <Button disabled={saving} type="submit">
                    {saving ? <Spinner /> : "Save"}
                </Button>
            </DialogFooter>
        </ManagedForm>
    );
}
